/**
 * Questions against a quote — asked from the live page or logged here by the desk.
 * Ask Isaac drafts on request; publishing stamps "Reviewed by Engineering" and,
 * when there is an email on file, sends the answer to the asker.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Sparkles, CheckCircle2, Ban, Printer, Plus, ShieldCheck, MailWarning,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const BADGE = {
  awaiting_review: "bg-amber-100 text-amber-800 border-amber-300",
  published: "bg-emerald-100 text-emerald-800 border-emerald-300",
  declined: "bg-slate-100 text-slate-600 border-slate-300",
};

const SOURCES = ["meeting", "email", "phone", "RFI", "site walk", "portal"];

const EMPTY = {
  asker_name: "", asker_org: "", asker_role: "", asker_email: "",
  source: "meeting", question: "",
};

export const QuoteQuestionsDialog = ({ quote, token, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/quote-questions?quote_id=${quote.id}`, { headers });
      setRows(r.data.questions || []);
      setDrafts(Object.fromEntries((r.data.questions || []).map((q) => [q.id, q.answer || ""])));
    } catch {
      toast.error("Could not load the questions");
    } finally {
      setLoading(false);
    }
  }, [quote.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const addQuestion = async () => {
    if (form.asker_name.trim().length < 2) return toast.error("Who raised it? Add their name");
    if (form.question.trim().length < 10) return toast.error("Write the question out in full");
    setAdding(true);
    try {
      const body = {
        asker_name: form.asker_name.trim(),
        asker_org: form.asker_org.trim(),
        asker_role: form.asker_role.trim(),
        source: form.source,
        question: form.question.trim(),
        ...(form.asker_email.includes("@") ? { asker_email: form.asker_email.trim() } : {}),
      };
      await axios.post(`${API}/admin/quote-questions?quote_id=${quote.id}`, body, { headers });
      toast.success("Question logged — draft it with Ask Isaac when you are ready");
      setForm(EMPTY);
      setShowAdd(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log that question");
    } finally {
      setAdding(false);
    }
  };

  const save = async (id, status) => {
    setBusy(id);
    try {
      const r = await axios.patch(`${API}/admin/quote-questions/${id}`,
        { answer: drafts[id], ...(status ? { status } : {}) }, { headers });
      toast.success(status === "published"
        ? (r.data?.asker_email
          ? "Published — stamped by Engineering, emailed to the asker and on the addendum"
          : "Published — stamped by Engineering and on the addendum (no email on file)")
        : status === "declined" ? "Marked as declined" : "Answer saved");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setBusy(null);
    }
  };

  const redraft = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/admin/quote-questions/${id}/redraft`, {}, { headers });
      setDrafts((p) => ({ ...p, [id]: r.data.answer || "" }));
      toast.success("Ask Isaac drafted the answer — review it before publishing");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not draft that");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start justify-center p-3 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="quote-qa-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] font-bold text-blue-700">{quote.quote_number}</p>
            <h3 className="text-base font-bold text-slate-900">Stakeholder questions</h3>
            <p className="text-[12px] text-slate-500">
              Asked from the live quote or logged here off a meeting, call or RFI. Ask Isaac drafts
              from the scope; publishing stamps <strong>Reviewed by Engineering</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => openAuthedDoc(`/quotes/${quote.id}/addendum`, { token })}
              data-testid="quote-qa-addendum">
              <Printer className="w-3.5 h-3.5 mr-1" /> Addendum
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
              data-testid="quote-qa-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="px-5 pt-4">
          {!showAdd ? (
            <Button size="sm" variant="outline" className="h-8 text-xs w-full border-dashed"
              onClick={() => setShowAdd(true)} data-testid="qa-add-open">
              <Plus className="w-3.5 h-3.5 mr-1" /> Log a stakeholder question
            </Button>
          ) : (
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-2"
              data-testid="qa-add-form">
              <p className="text-[10px] font-bold uppercase tracking-wider text-blue-800">
                Log a question raised off-platform
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.asker_name} onChange={(e) => set("asker_name", e.target.value)}
                  placeholder="Stakeholder name *" data-testid="qa-add-name" />
                <Input value={form.asker_org} onChange={(e) => set("asker_org", e.target.value)}
                  placeholder="Organisation / department" data-testid="qa-add-org" />
                <Input value={form.asker_role} onChange={(e) => set("asker_role", e.target.value)}
                  placeholder="Role (owner rep, contractor…)" data-testid="qa-add-role" />
                <Input value={form.asker_email} onChange={(e) => set("asker_email", e.target.value)}
                  placeholder="Email (optional)" data-testid="qa-add-email" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Came in via
                </span>
                <select value={form.source} onChange={(e) => set("source", e.target.value)}
                  className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                  data-testid="qa-add-source">
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Textarea rows={4} value={form.question}
                onChange={(e) => set("question", e.target.value)}
                placeholder="The question as it was asked — name the phase, line item or code it is asked against. *"
                className="text-[13px]" data-testid="qa-add-question" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" className="h-8 text-xs"
                  onClick={() => { setShowAdd(false); setForm(EMPTY); }}
                  data-testid="qa-add-cancel">Cancel</Button>
                <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                  disabled={adding} onClick={addQuestion} data-testid="qa-add-submit">
                  {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    : <Plus className="w-3.5 h-3.5 mr-1" />}
                  Log question
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <p className="text-center text-slate-400 py-6">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </p>
          )}
          {!loading && !rows.length && (
            <p className="text-center text-slate-400 py-8 text-sm" data-testid="quote-qa-empty">
              No questions yet. They arrive from the quote's live page, or log one above.
            </p>
          )}
          {rows.map((q) => (
            <div key={q.id} className="rounded-xl border border-slate-200 overflow-hidden"
              data-testid={`qa-item-${q.id}`}>
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{q.asker_name}</p>
                    <p className="text-[11px] text-slate-500">
                      {q.asker_org || "—"}{q.asker_role ? ` · ${q.asker_role}` : ""}
                      {q.asker_email ? ` · ${q.asker_email}` : ""}
                      {q.source ? ` · via ${q.source}` : ""}
                    </p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${BADGE[q.status] || BADGE.declined}`}>
                    {(q.status || "").replace("_", " ")}
                  </span>
                </div>
                <p className="text-[13px] text-slate-800 mt-2 whitespace-pre-wrap">{q.question}</p>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Answer {q.ai_model ? `· drafted by ${q.ai_model}` : ""}
                  </p>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    disabled={busy === q.id} onClick={() => redraft(q.id)}
                    data-testid={`qa-redraft-${q.id}`}>
                    {busy === q.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      : <Sparkles className="w-3 h-3 mr-1" />}
                    {q.answer ? "Redraft" : "Draft with Ask Isaac"}
                  </Button>
                </div>
                {q.ai_error && (
                  <p className="text-[11px] text-rose-700">Draft failed: {q.ai_error}</p>
                )}
                <Textarea rows={7} value={drafts[q.id] ?? ""}
                  onChange={(e) => setDrafts((p) => ({ ...p, [q.id]: e.target.value }))}
                  className="text-[13px] leading-relaxed"
                  data-testid={`qa-answer-${q.id}`} />
                {!q.asker_email && q.status !== "published" && (
                  <p className="text-[11px] text-amber-700 flex items-center gap-1"
                    data-testid={`qa-no-email-${q.id}`}>
                    <MailWarning className="w-3.5 h-3.5" />
                    No email on file — publishing records it on the addendum only, nothing is sent.
                  </p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500"
                    disabled={busy === q.id} onClick={() => save(q.id, "declined")}
                    data-testid={`qa-decline-${q.id}`}>
                    <Ban className="w-3.5 h-3.5 mr-1" /> Decline
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                    disabled={busy === q.id} onClick={() => save(q.id)}
                    data-testid={`qa-save-${q.id}`}>
                    Save draft
                  </Button>
                  <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                    disabled={busy === q.id} onClick={() => save(q.id, "published")}
                    data-testid={`qa-publish-${q.id}`}>
                    {busy === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Publish · stamp Engineering
                  </Button>
                </div>
                {q.status === "published" && (
                  <p className="text-[11px] text-emerald-700 text-right flex items-center justify-end gap-1"
                    data-testid={`qa-stamp-${q.id}`}>
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Reviewed by Engineering — {q.reviewed_by_engineering || q.answered_by || "—"}
                    {q.engineering_reviewed_at ? ` · ${q.engineering_reviewed_at.slice(0, 10)}`
                      : q.published_at ? ` · ${q.published_at.slice(0, 10)}` : ""}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuoteQuestionsDialog;
